import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { CRM_ADMISSION_STATUS } from "../../lib/constants";
import { enumLabel } from "../../lib/crmLabels";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import {
  FolderCheck, Plus, Pencil, Trash2, FileCheck2, ArrowUp, ArrowDown, GripVertical, Home,
} from "lucide-react";

const emptyAdm = {
  prospect_id: "", prospect_name: "", status: "dossier_ouvert", entry_date: "",
  room_number: "", partner_id: "", documents: [], satisfaction: 0, post_admission_notes: "", post_followup_date: "", notes: "",
};

export default function AdmissionsTab({ prospects, onChange, readOnly }) {
  const { t } = useTranslation();
  const [admissions, setAdmissions] = useState([]);
  const [docs, setDocs] = useState([]);
  const [partners, setPartners] = useState([]);
  const [open, setOpen] = useState(false);
  const [dcOpen, setDcOpen] = useState(false);
  const [form, setForm] = useState(emptyAdm);
  const [edit, setEdit] = useState(null);
  const [newItem, setNewItem] = useState("");

  const load = async () => {
    try {
      const [a, d, p] = await Promise.all([api.get("/crm/admissions"), api.get("/crm/doc-checklist"), api.get("/crm/partners")]);
      setAdmissions(a.data); setDocs(d.data); setPartners(p.data.partners || p.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const activeDocs = useMemo(() => docs.filter((d) => d.active), [docs]);

  const openNew = () => {
    setForm({ ...emptyAdm, documents: activeDocs.map((d) => ({ label: d.label, received: false, date: "" })) });
    setEdit(null); setOpen(true);
  };
  const openEdit = (a) => { setForm({ ...emptyAdm, ...a, documents: (a.documents || []).map((x) => ({ ...x })) }); setEdit(a.id); setOpen(true); };

  const onProspect = (pid) => {
    const p = prospects.find((x) => x.id === pid);
    setForm((f) => ({ ...f, prospect_id: pid, prospect_name: p ? `${p.last_name} ${p.first_name}`.trim() : f.prospect_name }));
  };
  const toggleDoc = (idx, val) => setForm((f) => ({ ...f, documents: f.documents.map((c, i) => i === idx ? { ...c, received: !!val, date: val ? (c.date || new Date().toISOString().slice(0, 10)) : "" } : c) }));

  const save = async () => {
    if (!form.prospect_id && !form.prospect_name.trim()) return toast.error(t("crm.admission.selectProspectErr"));
    if (!(form.room_number || "").trim()) return toast.error(t("crm.admission.roomRequired"));
    if (!(form.entry_date || "").trim()) return toast.error(t("crm.admission.entryRequired"));
    try {
      if (edit) await api.put(`/crm/admissions/${edit}`, form);
      else await api.post("/crm/admissions", form);
      toast.success(t("crm.admission.saved")); setOpen(false); load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm(t("crm.admission.confirmDelete"))) return;
    await api.delete(`/crm/admissions/${id}`); toast.success(t("crm.admission.deleted")); load(); onChange?.();
  };

  const addItem = async () => {
    if (!newItem.trim()) return;
    await api.post("/crm/doc-checklist", { label: newItem.trim(), active: true });
    setNewItem(""); const d = await api.get("/crm/doc-checklist"); setDocs(d.data);
  };
  const updItem = async (it, patch) => {
    await api.put(`/crm/doc-checklist/${it.id}`, { label: patch.label ?? it.label, active: patch.active ?? it.active });
    const d = await api.get("/crm/doc-checklist"); setDocs(d.data);
  };
  const delItem = async (id) => { await api.delete(`/crm/doc-checklist/${id}`); const d = await api.get("/crm/doc-checklist"); setDocs(d.data); };
  const move = async (idx, dir) => {
    const arr = [...docs]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]]; setDocs(arr);
    await api.post("/crm/doc-checklist/reorder", { ids: arr.map((x) => x.id) });
  };

  const recv = (a) => (a.documents || []).filter((d) => d.received).length;
  const partnerName = (id) => partners.find((p) => p.id === id)?.name || "";
  const stChip = (k) => { const d = CRM_ADMISSION_STATUS[k] || CRM_ADMISSION_STATUS.dossier_ouvert; return (
    <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium" style={{ color: d.text, background: d.bg }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: d.dot }} />{enumLabel(t, "admissionStatus", k, CRM_ADMISSION_STATUS)}</span>); };

  return (
    <div className="space-y-4" data-testid="crm-admissions-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2"><FolderCheck className="h-4 w-4 text-sky-400" /> {t("crm.admission.title")}</h3>
        <div className="flex gap-2">
          {!readOnly && <Button variant="outline" onClick={() => setDcOpen(true)} data-testid="crm-manage-docs"><FileCheck2 className="h-4 w-4 mr-2" /> {t("crm.admission.customizeDocs")}</Button>}
          {!readOnly && <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-500" data-testid="crm-add-admission"><Plus className="h-4 w-4 mr-2" /> {t("crm.admission.addBtn")}</Button>}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {admissions.map((a) => {
          const total = (a.documents || []).length; const done = recv(a);
          const pct = total ? Math.round(done / total * 100) : 0;
          return (
            <div key={a.id} className="rounded-2xl border border-white/10 p-4 bg-white/[0.02]" data-testid={`crm-admission-${a.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium truncate">{a.prospect_name || "—"}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2">
                    {a.room_number && <span className="flex items-center gap-1"><Home className="h-3 w-3" />{a.room_number}</span>}
                    {a.entry_date && <span>{t("crm.admission.entryDate")} : {a.entry_date}</span>}
                  </div>
                </div>
                {stChip(a.status)}
              </div>
              <div className="mt-3">
                <div className="flex items-center justify-between text-xs text-foreground/70 mb-1"><span>{t("crm.admission.documents")}</span><span>{done}/{total}</span></div>
                <div className="h-2 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct}%`, background: pct === 100 ? "#22c55e" : "#0ea5e9" }} /></div>
              </div>
              {a.partner_id && <div className="text-[11px] text-foreground/50 mt-2 truncate">{t("crm.admission.prescriberLabel", { name: partnerName(a.partner_id) })}</div>}
              <div className="mt-3 flex justify-end gap-1">
                {!readOnly && <><Button size="icon" variant="ghost" onClick={() => openEdit(a)} data-testid={`crm-edit-admission-${a.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="text-rose-400" onClick={() => del(a.id)}><Trash2 className="h-4 w-4" /></Button></>}
              </div>
            </div>
          );
        })}
        {!admissions.length && <p className="text-muted-foreground text-sm col-span-full py-8 text-center">{t("crm.admission.empty")}</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{edit ? t("crm.admission.edit") : t("crm.admission.new")}</DialogTitle>
            <DialogDescription className="sr-only">{t("crm.admission.title")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2"><Label>{t("crm.admission.futureResident")} <span className="text-rose-400">*</span></Label>
                <Select value={form.prospect_id || "none"} onValueChange={(v) => onProspect(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="crm-adm-prospect"><SelectValue placeholder={t("crm.admission.selectProspect")} /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>
                    {prospects.map((p) => <SelectItem key={p.id} value={p.id}>{p.last_name} {p.first_name}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label>{t("crm.admission.dossierStatus")}</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="crm-adm-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(CRM_ADMISSION_STATUS).map((k) => <SelectItem key={k} value={k}>{enumLabel(t, "admissionStatus", k, CRM_ADMISSION_STATUS)}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label>{t("crm.admission.entryDate")} <span className="text-rose-400">*</span></Label><Input type="date" value={form.entry_date || ""} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} data-testid="crm-adm-entry" /></div>
              <div className="space-y-1.5"><Label>{t("crm.admission.room")} <span className="text-rose-400">*</span></Label><Input value={form.room_number || ""} onChange={(e) => setForm({ ...form, room_number: e.target.value })} data-testid="crm-adm-room" placeholder="Ex : 101" /></div>
              <div className="space-y-1.5"><Label>{t("crm.admission.prescriber")}</Label>
                <Select value={form.partner_id || "none"} onValueChange={(v) => setForm({ ...form, partner_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>
                    {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-sky-300 mb-2 flex items-center gap-1"><FileCheck2 className="h-3.5 w-3.5" /> {t("crm.admission.docChecklist")}</p>
              <div className="space-y-1.5 rounded-xl border border-white/10 p-3 bg-white/[0.02]">
                {form.documents.map((c, i) => (
                  <label key={i} className="flex items-center gap-2 cursor-pointer text-sm py-0.5">
                    <Checkbox checked={c.received} onCheckedChange={(v) => toggleDoc(i, v)} data-testid={`crm-adm-doc-${i}`} />
                    <span className={c.received ? "line-through text-foreground/50" : ""}>{c.label}</span>
                    {c.received && c.date && <span className="ml-auto text-[11px] text-emerald-400">{c.date}</span>}
                  </label>
                ))}
                {!form.documents.length && <p className="text-xs text-muted-foreground">{t("crm.admission.noActive")}</p>}
              </div>
            </div>
            <div className="space-y-1.5"><Label>{t("crm.admission.notes")}</Label><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>

            {form.status === "entre" && (
              <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.05] p-3 space-y-3" data-testid="crm-adm-postadmission">
                <p className="text-xs uppercase tracking-wider font-semibold text-emerald-300">{t("crm.admission.postAdmission")}</p>
                <div className="grid grid-cols-2 gap-3 items-end">
                  <div className="space-y-1.5">
                    <Label>{t("crm.admission.satisfaction")}</Label>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button key={n} type="button" onClick={() => setForm({ ...form, satisfaction: n })} data-testid={`crm-adm-star-${n}`}
                          className={`h-7 w-7 rounded-md flex items-center justify-center ${n <= (form.satisfaction || 0) ? "text-amber-400" : "text-white/25"}`}>★</button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-1.5"><Label>{t("crm.admission.nextFollowup")}</Label><Input type="date" value={form.post_followup_date || ""} onChange={(e) => setForm({ ...form, post_followup_date: e.target.value })} /></div>
                </div>
                <div className="space-y-1.5"><Label>{t("crm.admission.followupNotes")}</Label><Textarea value={form.post_admission_notes || ""} onChange={(e) => setForm({ ...form, post_admission_notes: e.target.value })} placeholder={t("crm.admission.followupPlaceholder")} /></div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("crm.actions.cancel")}</Button>
            <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-500" data-testid="crm-save-admission">{t("crm.actions.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dcOpen} onOpenChange={setDcOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("crm.admission.mgrTitle")}</DialogTitle>
            <DialogDescription>{t("crm.admission.mgrDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {docs.map((it, i) => (
              <div key={it.id} className="flex items-center gap-2 rounded-lg border border-white/10 p-2 bg-white/[0.02]" data-testid={`crm-doc-item-${it.id}`}>
                <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
                <Input value={it.label} onChange={(e) => setDocs(docs.map((x) => x.id === it.id ? { ...x, label: e.target.value } : x))}
                  onBlur={(e) => updItem(it, { label: e.target.value })} className="h-8" />
                <Switch checked={it.active} onCheckedChange={(v) => updItem(it, { active: v })} data-testid={`crm-doc-active-${it.id}`} />
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => move(i, 1)} disabled={i === docs.length - 1}><ArrowDown className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-400" onClick={() => delItem(it.id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
          <div className="flex gap-2 pt-2">
            <Input value={newItem} onChange={(e) => setNewItem(e.target.value)} placeholder={t("crm.admission.newDoc")} onKeyDown={(e) => e.key === "Enter" && addItem()} data-testid="crm-doc-new" />
            <Button onClick={addItem} className="bg-emerald-600 hover:bg-emerald-500" data-testid="crm-doc-add"><Plus className="h-4 w-4 mr-1" /> {t("crm.actions.add")}</Button>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDcOpen(false)}>{t("crm.actions.close")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
